/**
 * Money visibly moving — Blockari's `shell/money.ts`, in 21 Wild's kit, with the win flight as a stream.
 *
 *   STAKE  the balance pill's coin arcs to the pot card's coin (the source dims to a ghost holding its
 *          slot, so there is only ever one coin), the pill pulses, and the number counts DOWN to the new
 *          balance.
 *   WIN    a stream of coins (6–12, scaled gently by the pot; 3 on the low quality tier), one every
 *          ~40 ms, arcs from the `+N $CHAIN` figure into the balance pill. Each landing pulses the pill
 *          and steps the number UP, and the last lands exactly on the server's balance.
 *   REFUND a single coin, the same path with one coin.
 *
 * The flight is decoration; the number is the server's. Everything the pill SHOWS goes through
 * `BalanceDisplay`, which holds the display for the flight and hands back the truth, exactly, at the end
 * — so a refresh arriving before, during or after cannot flash a wrong value (see balance-display.ts).
 *
 * This file is the logic, with every DOM, sound and storage dependency injected so the ordering rules
 * (one stream per payout, nothing on a loss, reduced motion resolves at once) are unit-tested without a
 * browser. `money-dom.ts` supplies the real ones.
 */

import { countSteps, type BalanceDisplay, type Hold } from '@/ui/balance-display';

/** Blockari's numbers, unchanged. */
export const COIN_FLY_MS = 480;
export const PILL_PULSE_MS = 420;
/** Gap between coins in a stream. */
export const STAGGER_MS = 40;
/** The count of a stake's number going down, after the coin has left. */
export const STAKE_COUNT_MS = 300;
/**
 * The most a stake's flight may hold the player up. The round or the challenge page waits for the coin to
 * land, so a flight that cannot finish (a hidden tab pauses animation frames) must never be able to stall a
 * paid take: after this the display is released and the caller carries on.
 */
export const STAKE_GUARD_MS = 1800;

export type QualityTier = 'high' | 'medium' | 'low';

/** Coins in a win stream: 6 at a small pot rising gently to 12 at a large one; 3 on the low tier. */
export function streamSize(pot: number, tier: QualityTier = 'high'): number {
  if (tier === 'low') return 3;
  const p = Math.max(0, Number.isFinite(pot) ? pot : 0);
  // 6 at ≤20, 12 by 400; log-ish so 200 is not twice 20's stream.
  const t = Math.min(1, Math.max(0, Math.log10(Math.max(p, 20) / 20) / Math.log10(20)));
  return Math.round(6 + t * 6);
}

/** Where a coin starts or ends. An element to read a rect from, or a point (for a stream's origin). */
export type Anchor = Element | { x: number; y: number; size: number };

export interface SeenStore {
  has(id: string): boolean;
  add(id: string): void;
}

export interface FlightDeps {
  display: BalanceDisplay;
  reduced(): boolean;
  tier(): QualityTier;
  /**
   * One coin from `from` to `to` (resolves on landing; at once if either end is missing). `via` is the
   * waypoint the arc passes through, in viewport px, instead of the default lifted midpoint.
   */
  fly(from: Anchor, to: Anchor, options?: { ghost?: boolean; via?: { x: number; y: number } }): Promise<void>;
  /** The real balance pill's coin on the visible screen, or null when the screen has no pill. */
  pillCoin(): Element | null;
  /** Pulse the pill the coin is landing in. */
  pulse(target: Element | null): void;
  /**
   * The pill on the results screen, which has no top bar: always laid out, unseen until a payout is in the air,
   * and then it stays. `show` brings it in and resolves when it is; `coin` is its coin.
   */
  resultsPill: { show(): Promise<void>; coin(): Element | null };
  /** Landing `step` of `total`: the sound. */
  land(step: number, total: number): void;
  seen: SeenStore;
  /**
   * The box of the figure's TEXT (the ink of `+200 $CHAIN`), when it can be told apart from the element's
   * box — a block element spans the whole plate, and the stream should leave the words, not the plate's edge.
   */
  textBox?(figure: Element): { left: number; right: number; top: number; bottom: number } | null;
  /** Wait `ms` (the stagger, the pulse). */
  sleep(ms: number): Promise<void>;
  /** Run `frame(t)` for t in 0..1 over `ms`; resolves when done. */
  tween(ms: number, frame: (t: number) => void): Promise<void>;
}

export interface StakeFlight {
  /** Fly to the pot card's coin, pulse, count down, and let the display go. Never throws. */
  finish(potCoin: Element | null): Promise<void>;
}

/** A payout announced by a toast (a win settled while the player was elsewhere, or a refund). */
export interface PayoutFlight {
  /** Fly one coin from `figure` (the toast's amount) into the pill and let the display go. Never throws. */
  finish(figure: Element | null): Promise<void>;
}

/** A toast that never shows must not hold the pill: after this the display is released. */
export const PAYOUT_GUARD_MS = 2000;

export interface WinFlight {
  /** The payout's id — one stream per id. */
  id: string;
  /** What was paid. */
  payout: number;
  /** The pot (stake × 2), for the size of the stream. */
  pot: number;
  /** The `+N $CHAIN` figure the coins leave from. */
  figure: Element | null;
  /** Fly to the top bar's pill (default) or to the results screen's, which the screen has in place of one. */
  toResultsPill?: boolean;
  /** A refund is one coin, not a stream. */
  single?: boolean;
  /**
   * The payout is ALREADY in the balance the pill shows (a settled win seen later, on its challenge page):
   * count up from the balance minus the payout, instead of from what was on screen a moment ago.
   */
  included?: boolean;
  /**
   * Hold the number at its starting point now, but let the first coin leave this many ms later. For a page
   * that has just appeared (the challenge page): the pill must already read the balance without the payout
   * as the page comes in, and the coins should not start until it has settled.
   */
  delayMs?: number;
}

export function createFlights(deps: FlightDeps) {
  /** Ids with a stream in the air or already played this session. */
  const live = new Set<string>();

  return {
    /**
     * A stake is about to leave the balance. Call BEFORE the account is told the new balance, so the pill
     * is already holding when the number changes; then call `finish` with the pot card's coin.
     */
    beginStake(): StakeFlight {
      const from = deps.display.shown;
      const hold = deps.display.hold(from);
      let done = false;
      return {
        async finish(potCoin) {
          if (done) return;
          done = true;
          try {
            const pill = deps.pillCoin();
            const to = hold.truth;
            if (deps.reduced() || !pill || !potCoin || from === null || to === null) return;
            const flight = (async (): Promise<void> => {
              await deps.fly(pill, potCoin, { ghost: true });
              deps.pulse(pill.closest('.w21-balance') ?? pill);
              // Down to the new balance: the coin has left, and the number follows it.
              await deps.tween(STAKE_COUNT_MS, (t) => hold.show(from + (hold.truth! - from) * t));
            })();
            await Promise.race([flight, deps.sleep(STAKE_GUARD_MS)]);
          } catch {
            // Decoration must never cost the player their stake screen.
          } finally {
            hold.release();
          }
        },
      };
    },

    /**
     * A payout announced by a toast. Call BEFORE the account is told the new balance, so the pill holds; then
     * `finish` with the toast's amount once it is on screen. Returns null — and does nothing, marking
     * nothing — when this payout has been shown already (the results stream, the challenge page), when
     * motion is reduced, or when the screen has no pill: the caller then moves the number as it always did.
     */
    beginPayout(input: { id: string; payout: number }): PayoutFlight | null {
      const { id, payout } = input;
      if (!Number.isFinite(payout) || payout <= 0) return null;
      if (live.has(id) || deps.seen.has(id) || deps.reduced()) return null;
      if (!deps.pillCoin()) return null;
      const from = deps.display.shown;
      if (from === null) return null;
      live.add(id);
      deps.seen.add(id);
      const hold = deps.display.hold(from);
      let done = false;
      const finish = async (figure: Element | null): Promise<void> => {
        if (done) return;
        done = true;
        try {
          const pill = deps.pillCoin();
          if (!figure || !pill) return;
          const flight = (async (): Promise<void> => {
            // The toast's own coin is cloned and ghosted (one coin on screen, never two). With no coin to
            // clone — a refund's toast has only words — a coin is made at the toast's leading edge instead.
            const isCoin = figure.classList?.contains('w21-coin') === true;
            let launch: Anchor = figure;
            if (!isCoin && typeof figure.getBoundingClientRect === 'function') {
              const b = figure.getBoundingClientRect();
              launch = { x: b.left + 30, y: b.top + b.height / 2, size: 28 };
            }
            await deps.fly(launch, pill, isCoin ? { ghost: true } : {});
            deps.pulse(pill.closest('.w21-balance') ?? pill);
            deps.land(1, 1);
            // The truth has been told already (the caller moved the account first); it is what this ends on.
            hold.show(hold.truth !== null && hold.truth !== from ? hold.truth : from + payout);
            await deps.sleep(PILL_PULSE_MS);
          })();
          await Promise.race([flight, deps.sleep(PAYOUT_GUARD_MS)]);
        } catch {
          // Decoration only.
        } finally {
          hold.release(from + payout);
        }
      };
      // Nobody will ever call `finish` if the toast is not shown: do not hold the pill for it.
      void deps.sleep(PAYOUT_GUARD_MS).then(() => finish(null));
      return { finish };
    },

    /**
     * A payout landing. Returns false — doing nothing — if this payout has been shown already (once per
     * id, per session and per browser), if it paid nothing, or if there is nowhere to land.
     */
    async win(flight: WinFlight): Promise<boolean> {
      const { id, payout } = flight;
      if (!Number.isFinite(payout) || payout <= 0) return false;
      if (live.has(id) || deps.seen.has(id)) return false;
      live.add(id);
      deps.seen.add(id);

      // What the player last saw, before anything about this payout reached the pill — or, for a payout
      // that settled while they were away, the balance without it.
      const known = deps.display.truth ?? deps.display.shown;
      const from = flight.included && known !== null ? Math.max(0, known - payout) : deps.display.takeMark();
      // No motion: the number simply is the truth (BalanceDisplay shows it as soon as it is known).
      if (deps.reduced() || from === null) return false;

      const coins = flight.single ? 1 : streamSize(flight.pot, deps.tier());
      const realPill = flight.toResultsPill ? null : deps.pillCoin();
      const hold: Hold = deps.display.hold(from);
      try {
        if (flight.delayMs && flight.delayMs > 0) await deps.sleep(flight.delayMs);
        if (!realPill) await deps.resultsPill.show();
        const target: Element | null = realPill ?? deps.resultsPill.coin();
        const figure = flight.figure;
        if (!target || !figure) return true;

        // The stream leaves the RIGHT end of the `+N $CHAIN` line and swings out past the score before it
        // turns in to the pill, so it does not cross the headline. A little deterministic spread makes it a
        // stream rather than one coin drawn eleven times.
        const box = deps.textBox?.(figure) ?? figure.getBoundingClientRect();
        const cy = (box.top + box.bottom) / 2;
        const tbox = typeof target.getBoundingClientRect === 'function' ? target.getBoundingClientRect() : null;
        const ty = tbox ? tbox.top + tbox.height / 2 : cy - 120;
        const originAt = (i: number) => ({ x: box.right - 12 + ((i % 3) - 1) * 5, y: cy + ((i % 4) - 1.5) * 3, size: 22 });
        const viaAt = (i: number) => ({ x: box.right + 10 + (i % 3) * 4, y: (cy + ty) / 2 - 24 });
        // Where the number is heading: the server's, once it is known; the expected one until then.
        // A truth equal to what was seen is a truth not yet heard: the payout has moved the real number.
        const heading = (): number => (hold.truth !== null && hold.truth !== from ? hold.truth : from + payout);
        let landed = 0;
        const pulseTarget = (realPill ? (realPill.closest('.w21-balance') ?? realPill) : deps.resultsPill.coin()?.closest('.w21-balance')) ?? null;

        const flights: Promise<void>[] = [];
        for (let i = 0; i < coins; i += 1) {
          if (i > 0) await deps.sleep(STAGGER_MS);
          flights.push(
            deps.fly(originAt(i), target, { via: viaAt(i) }).then(() => {
              landed += 1;
              const steps = countSteps(from, heading(), coins);
              // Never backwards: a refresh that lands mid-stream can only move the goal, not the number.
              const next = Math.max(deps.display.shown ?? from, steps[landed - 1] ?? heading());
              hold.show(next);
              deps.pulse(pulseTarget);
              deps.land(landed, coins);
            }),
          );
        }
        await Promise.all(flights);
        await deps.sleep(PILL_PULSE_MS);
      } catch {
        // Fall through to the release: the truth is what stays on screen.
      } finally {
        hold.release(from + payout);
      }
      return true;
    },
  };
}

export type Flights = ReturnType<typeof createFlights>;
